import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Button from '../shared/Button';
import Card from '../shared/Card';
import './CTASection.css';

const CTASection = ({ 
  onBetaAccess, 
  onYouTubeVisit, 
  videoId = "qOHChghFtL0",
  showVideo = true 
}) => {
  const handleBetaAccess = () => {
    onBetaAccess?.();
  };

  const handleYouTubeVisit = () => {
    onYouTubeVisit?.();
  };

  return (
    <section className="cta-section" aria-labelledby="cta-title">
      <div className="container">
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 id="cta-title" className="cta-title">
            Ready to Transform Therapy Through Gaming?
          </h2>
          <p className="cta-subtitle">
            Join thousands of users already experiencing the power of adaptive gaming technology
          </p>

          {showVideo && (
            <motion.div
              className="video-section"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card variant="dark" hover={true} className="video-card">
                <div className="video-container">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                    title="enAbleGames Overview - Adaptive Gaming Technology"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                <div className="video-info">
                  <h3 className="video-title">enAbleGames Overview</h3>
                  <p className="video-description">
                    Watch and learn about our adaptive gaming platform designed for people with disabilities
                  </p>
                </div>
              </Card>
            </motion.div>
          )}

          <motion.div
            className="cta-actions"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="action-cards">
              <Card variant="glass" hover={true} className="action-card primary-action">
                <div className="action-content">
                  <div className="action-icon" aria-hidden="true">🚀</div>
                  <h3 className="action-title">Start Your Journey</h3>
                  <p className="action-description">
                    Get instant access to our beta portal and explore adaptive gaming
                  </p>
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={handleBetaAccess}
                    ariaLabel="Access beta portal to start using enAble Games"
                    className="action-button"
                  >
                    Access Beta Portal
                  </Button>
                </div>
              </Card>

              <Card variant="glass" hover={true} className="action-card secondary-action">
                <div className="action-content">
                  <div className="action-icon" aria-hidden="true">📺</div>
                  <h3 className="action-title">Learn More</h3>
                  <p className="action-description">
                    Visit our YouTube channel for tutorials and success stories
                  </p>
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={handleYouTubeVisit}
                    ariaLabel="Visit our YouTube channel for more information"
                    className="action-button"
                  >
                    Watch Videos
                  </Button>
                </div>
              </Card>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            className="trust-indicators"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="trust-item">
              <span className="trust-icon" aria-hidden="true">🏥</span>
              <span className="trust-text">Trusted by Healthcare Providers</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon" aria-hidden="true">🔒</span>
              <span className="trust-text">HIPAA Compliant Platform</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon" aria-hidden="true">♿</span>
              <span className="trust-text">ADA Accessibility Standards</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon" aria-hidden="true">📊</span>
              <span className="trust-text">Evidence-Based Results</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

CTASection.propTypes = {
  onBetaAccess: PropTypes.func,
  onYouTubeVisit: PropTypes.func,
  videoId: PropTypes.string,
  showVideo: PropTypes.bool
};

CTASection.defaultProps = {
  onBetaAccess: () => {},
  onYouTubeVisit: () => {
    window.open('https://www.youtube.com/@enablegames9807', '_blank', 'noopener,noreferrer');
  },
  videoId: "qOHChghFtL0",
  showVideo: true
};

export default CTASection;